/**
 * Development checks: warnings about what an update just did to the page that
 * a person using assistive technology would feel and a sighted developer
 * would not see. `pluginCrown` sets `__CROWN_DEV__` in a development build; a
 * production build replaces it with false and the checks drop out.
 */
import { hasGlobal } from '../ir/guards';

declare const __CROWN_DEV__: boolean | undefined;

/** The global object as a test sees it: it may carry the flag a build would define. */
interface DevGlobal {
  readonly __CROWN_DEV__?: boolean;
}

/**
 * Whether the development flag is on. A build replaces `__CROWN_DEV__` with a
 * literal and a test sets it on `scope`, the global object; anywhere else the
 * name is not declared at all, so only `typeof` may look at it.
 */
function devFlagOn(
  scope: typeof globalThis,
): scope is typeof globalThis & DevGlobal {
  return typeof __CROWN_DEV__ !== 'undefined' && __CROWN_DEV__ === true;
}

export function devChecks(): boolean {
  return devFlagOn(globalThis);
}

const NAMED_CONTROLS =
  'button, a[href], [role="button"], [role="link"], [role="menuitem"], [role="tab"]';

/** After a binding wrote `text`: warns when that left its control with no name at all. */
export function checkNameAfterWrite(text: Text): void {
  if (!devChecks() || text.data.trim() !== '') return;
  const control = text.parentElement?.closest<HTMLElement>(NAMED_CONTROLS);
  if (!control) return;
  if (
    control.getAttribute('aria-label') ||
    control.getAttribute('aria-labelledby') ||
    control.getAttribute('title')
  )
    return;
  if ((control.textContent ?? '').trim() !== '') return;
  console.warn(
    '[@crown/framework] a binding emptied the only name this control had; it now reads as nothing',
    control,
  );
}

/** Before an update removes `node`: warns when focus is inside it, as focus then falls back to the page's start. */
export function checkFocusBeforeRemoval(node: Node): void {
  if (!devChecks() || !hasGlobal('document')) return;
  const active = document.activeElement;
  if (!active || active === document.body || !node.contains(active)) return;
  console.warn(
    '[@crown/framework] an update removed the element that had focus; move focus somewhere that stays (trapFocus, or the next item) before removing it',
    active,
  );
}
